import { Link } from "react-router-dom";
import {
  ShieldCheck,
  FileCheck,
  Fingerprint,
  FlaskConical,
  Microscope,
  Check,
  X,
  AlertTriangle,
  ArrowRight,
  Headphones,
  Droplet,
  CalendarCheck,
  Files,
} from "lucide-react";

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/c63dc54c-84c8-4f10-bcbd-d8c0b2d53297/images/e2422c2f1844a178879bcc026bac3a325ade6a5f771c4607a64958d83c650c18.jpeg";

const TIMELINE = [
  {
    day: "DAY 1",
    testid: "timeline-day-1",
    phase: "The order arrives",
    body:
      "No COA link or batch number on the label, just a PDF on the product page with no lab name behind it. It looks fine. Most bad batches do.",
  },
  {
    day: "DAY 7",
    testid: "timeline-day-7",
    phase: "The questions start",
    body:
      "The purity percentage isn't listed anywhere you can verify, so you're not sure what concentration you're actually working with, and your protocol is already underway.",
  },
  {
    day: "DAY 14",
    testid: "timeline-day-14",
    phase: "The data doesn't add up",
    body:
      "Results come back inconsistent. Is it the protocol… or the compound? Without verified batch data, there's no way to isolate the variable.",
  },
  {
    day: "DAY 30",
    testid: "timeline-day-30",
    phase: "You go back to the supplier",
    body:
      "You get no response, or one that doesn't address the documentation gap. The PDF leads nowhere, and the batch number isn't on any third party record.",
  },
  {
    day: "DAY 45",
    testid: "timeline-day-45",
    phase: "Back to square one",
    body:
      "A month and a half of research compromised by an unverifiable compound. The cheaper price tag is long forgotten, the real cost is time, data integrity, and starting over with a supplier that actually shows its work.",
  },
];

const COMPARE = [
  {
    label: "Testing method",
    bad: 'A vague "tested" claim with no method named',
    good: "HPLC for purity + mass spec for identity, named on every lot",
  },
  {
    label: "Purity",
    bad: 'A blanket "99%+" with nothing to verify it against',
    good: "A real purity figure tied to your exact lot",
  },
  {
    label: "Traceability",
    bad: "No batch/lot ID, or one that leads nowhere",
    good: "A unique lot ID matched to your vial's certificate",
  },
  {
    label: "Who tests it",
    bad: "Self-graded, or no laboratory named at all",
    good: "An independent, third party laboratory, we don't grade our own work",
  },
  {
    label: "When you get the paperwork",
    bad: "Missing on arrival, you chase it after something goes wrong",
    good: "Documented before it's listed; a lot specific COA ships with your order",
  },
  {
    label: "Support",
    bad: "Silence when the numbers don't add up",
    good: "Real, US based people who answer",
  },
];

const LOOK_FOR = [
  { icon: FlaskConical, text: "A named testing method, not just the word \u201ctested.\u201d" },
  { icon: Microscope, text: "Identity confirmation (mass spec), not only a purity number." },
  { icon: Fingerprint, text: "A lot/batch ID you can trace back to real paperwork." },
  { icon: ShieldCheck, text: "Independent, third party verification, not self-reported." },
  { icon: FileCheck, text: "A COA matched to your exact vial, provided with the order." },
  { icon: Headphones, text: "A supplier who documents up front, not one who scrambles after." },
];

const LIVE_VERIFIABLE = [
  { icon: Files, label: "Batch Numbers", sub: "A unique lot ID matched to the exact vial you receive." },
  { icon: Droplet, label: "Purity Percentages", sub: "A real HPLC purity figure, tied to your batch, not averaged." },
  { icon: CalendarCheck, label: "Test Dates", sub: "When the lot was tested, dated on the certificate, never vague." },
];

const COA_POINTS = [
  { n: "01", title: "HPLC purity", desc: "Reverse-phase chromatography quantifies purity, the headline number on every certificate." },
  { n: "02", title: "Mass spec identity", desc: "Mass-to-charge analysis confirms the compound is exactly what the label claims." },
  { n: "03", title: "Net peptide content", desc: "The actual peptide mass per vial, not just gross weight including salts and water." },
  { n: "04", title: "Endotoxin", desc: "Screened to confirm bacterial endotoxin sits below a conservative threshold." },
  { n: "05", title: "Appearance", desc: "Visual confirmation of the lyophilized cake or solution against spec." },
  { n: "06", title: "Heavy metals", desc: "Tested to verify trace metals remain within safe analytical limits." },
  { n: "07", title: "Lot archival", desc: "Every batch carries a unique lot ID linked to its certificate and kept on file." },
];

export default function Standards() {
  return (
    <div className="bg-[#070B1C]" data-testid="standards-page">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(circle at 78% 42%, rgba(46,144,255,0.20), transparent 55%)" }} />
        <div className="relative max-w-3xl mx-auto px-5 lg:px-8 pt-20 lg:pt-28 pb-16 lg:pb-24">
          <div>
            <p className="text-[10px] tracking-[0.35em] uppercase font-bold text-electric mb-6 fade-up">
              Before you buy
            </p>
            <h1
              className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tighter uppercase leading-[0.95] fade-up"
              style={{ animationDelay: "0.05s" }}
            >
              Not all batches are <br className="hidden sm:block" />created equal.
            </h1>
            <p
              className="text-base md:text-lg text-white/70 mt-8 max-w-xl leading-relaxed fade-up"
              style={{ animationDelay: "0.12s" }}
            >
              An unverified peptide from an undocumented supplier costs more than the price tag suggests.
              Here's what you're actually paying for when you choose Century Labs, and what it costs you
              when the documentation isn't there to back it up.
            </p>
            <div className="flex flex-wrap gap-3 mt-9 fade-up" style={{ animationDelay: "0.18s" }}>
              <Link
                to="/"
                className="btn-primary px-8 py-4 text-xs tracking-[0.2em] uppercase font-bold inline-flex items-center gap-2"
                data-testid="standards-hero-cta"
              >
                Shop Century Labs <ArrowRight size={15} />
              </Link>
              <a
                href="#timeline"
                className="btn-ghost px-8 py-4 text-xs tracking-[0.2em] uppercase font-bold inline-flex items-center"
                data-testid="standards-checklist-cta"
              >
                See the hidden cost
              </a>
            </div>
            <p className="mt-8 text-[10px] tracking-[0.2em] uppercase font-bold text-white/40 fade-up" style={{ animationDelay: "0.24s" }}>
              Free ground shipping over $200 · Fast domestic shipping
            </p>
          </div>
        </div>
      </section>

      {/* Intro band */}
      <section className="border-b border-white/10 bg-[#0A0F26]">
        <div className="max-w-[1100px] mx-auto px-5 lg:px-8 py-14 lg:py-16">
          <p className="text-lg md:text-2xl text-white leading-relaxed font-display max-w-3xl">
            Price comparisons between peptide suppliers only tell part of the story. The part most
            buyers miss is <span className="text-electric">what's behind the price</span>, and what
            happens when the paperwork isn't there when it matters.
          </p>
        </div>
      </section>

      {/* Timeline, vertical zig-zag */}
      <section id="timeline" className="max-w-[1100px] mx-auto px-5 lg:px-8 py-16 lg:py-24" data-testid="standards-timeline">
        <div className="flex items-center gap-3 mb-3">
          <AlertTriangle size={18} className="text-electric" />
          <span className="text-[10px] tracking-[0.3em] uppercase font-bold text-electric">
            The cost of an undocumented order
          </span>
        </div>
        <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight uppercase leading-none mb-16">
          How it actually plays out
        </h2>

        <div className="relative">
          {/* dashed spine */}
          <div
            className="absolute top-0 bottom-0 left-[27px] md:left-1/2 md:-translate-x-1/2 border-l-2 border-dashed border-electric/25"
            aria-hidden="true"
          />
          <div className="space-y-8 md:space-y-16">
            {TIMELINE.map((t, i) => {
              const left = i % 2 === 0;
              return (
                <div key={t.day} className="relative" data-testid={t.testid}>
                  {/* glowing node */}
                  <span
                    className="absolute left-[27px] md:left-1/2 -translate-x-1/2 top-4 z-10"
                    aria-hidden="true"
                  >
                    <span className="block w-5 h-5 rounded-full bg-electric ring-4 ring-electric/15 shadow-[0_0_18px_5px_rgba(46,144,255,0.45)]" />
                  </span>

                  <div className="md:grid md:grid-cols-2 md:gap-14 md:items-center">
                    <div className={left ? "md:col-start-1" : "md:col-start-2"}>
                      <div className="pl-16 md:pl-0">
                        <div
                          className={`border border-white/12 bg-[#0B1024] p-6 lg:p-7 sq-lg fade-up ${left ? "md:text-right" : ""}`}
                          style={{ animationDelay: `${0.06 * i}s` }}
                        >
                          <div className={`flex ${left ? "md:justify-end" : ""}`}>
                            <span className="inline-flex items-center px-4 py-1.5 rounded-full border border-electric/40 bg-electric/10 font-display font-bold text-sm tracking-[0.15em] text-electric">
                              {t.day}
                            </span>
                          </div>
                          <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-white/55 mt-4 mb-2">
                            {t.phase}
                          </div>
                          <p className="text-sm text-white/60 leading-relaxed">{t.body}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Comparison */}
      <section className="border-t border-white/10 bg-[#0A0F26]">
        <div className="max-w-[1100px] mx-auto px-5 lg:px-8 py-16 lg:py-24" data-testid="standards-compare">
          <p className="text-[10px] tracking-[0.3em] uppercase font-bold text-electric mb-3">
            Documented, not claimed
          </p>
          <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight uppercase leading-none mb-12">
            The Century Labs difference
          </h2>

          <div className="border border-white/12 sq-lg overflow-hidden">
            {/* header row */}
            <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr] bg-[#0B1024] border-b border-white/12">
              <div className="hidden md:flex items-center gap-2 px-6 py-4 border-r border-white/10">
                <X size={15} className="text-red-400" />
                <span className="text-[10px] tracking-[0.25em] uppercase font-bold text-white/50">
                  Undocumented supplier
                </span>
              </div>
              <div className="flex items-center gap-2 px-6 py-4">
                <ShieldCheck size={15} className="text-electric" />
                <span className="text-[10px] tracking-[0.25em] uppercase font-bold text-electric">
                  Century Labs
                </span>
              </div>
            </div>

            {COMPARE.map((row, i) => (
              <div
                key={row.label}
                className={`grid grid-cols-1 md:grid-cols-[1fr_1fr] ${i % 2 ? "bg-[#0A0F26]" : "bg-[#0B1024]"}`}
                data-testid={`compare-row-${i}`}
              >
                <div className="px-6 py-5 border-b md:border-b-0 md:border-r border-white/10">
                  <div className="text-[9px] tracking-[0.25em] uppercase text-white/35 mb-1.5">
                    {row.label}
                  </div>
                  <div className="flex items-start gap-2.5">
                    <X size={16} className="text-red-400/80 shrink-0 mt-0.5" />
                    <span className="text-sm text-white/55 leading-snug">{row.bad}</span>
                  </div>
                </div>
                <div className="px-6 py-5 border-b border-white/10 md:border-b-0">
                  <div className="text-[9px] tracking-[0.25em] uppercase text-electric/60 mb-1.5 md:opacity-0 md:select-none">
                    {row.label}
                  </div>
                  <div className="flex items-start gap-2.5">
                    <Check size={16} className="text-electric shrink-0 mt-0.5" />
                    <span className="text-sm text-white leading-snug">{row.good}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Checklist */}
      <section id="checklist" className="max-w-[1100px] mx-auto px-5 lg:px-8 py-16 lg:py-24" data-testid="standards-checklist">
        <p className="text-[10px] tracking-[0.3em] uppercase font-bold text-electric mb-3">
          A quick gut check
        </p>
        <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight uppercase leading-none mb-4">
          What to look for in a research supplier
        </h2>
        <p className="text-base text-white/60 max-w-2xl leading-relaxed mb-12">
          Run any supplier, including us, through this list before you spend a dollar. If they can't
          answer these plainly, that silence is your answer.
        </p>

        <div className="grid sm:grid-cols-2 gap-4">
          {LOOK_FOR.map((item, i) => {
            const Icon = item.icon;
            return (
              <div
                key={i}
                className="flex items-start gap-4 border border-white/12 bg-[#0B1024] p-6 sq-lg fade-up"
                style={{ animationDelay: `${0.05 * i}s` }}
                data-testid={`checklist-item-${i}`}
              >
                <div className="w-10 h-10 shrink-0 flex items-center justify-center bg-electric/10 border border-electric/30 sq-md">
                  <Icon size={18} className="text-electric" />
                </div>
                <p className="text-sm text-white/80 leading-relaxed pt-1.5">{item.text}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Seven point COA panel */}
      <section className="border-t border-white/10 bg-[#070B1C]">
        <div className="max-w-[1100px] mx-auto px-5 lg:px-8 py-16 lg:py-24" data-testid="coa-seven-point">
          <p className="text-[10px] tracking-[0.3em] uppercase font-bold text-electric mb-3">The seven-point panel</p>
          <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight uppercase leading-none mb-4">
            What every certificate covers
          </h2>
          <p className="text-base text-white/60 max-w-2xl leading-relaxed mb-12">
            Each lot is assayed by an independent laboratory before it's listed, and the signed
            certificate is matched to the exact vial you receive.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {COA_POINTS.map((p, i) => (
              <div
                key={p.n}
                className="border border-white/12 bg-[#0B1024] p-6 sq-lg fade-up"
                style={{ animationDelay: `${0.05 * i}s` }}
                data-testid={`coa-point-${p.n}`}
              >
                <div className="font-display font-bold text-2xl text-electric/30 mb-3">{p.n}</div>
                <h3 className="font-display font-bold text-base uppercase tracking-tight text-white mb-2">{p.title}</h3>
                <p className="text-sm text-white/55 leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Price-alone statement + Live & Verifiable */}
      <section className="border-t border-white/10 bg-[#0A0F26]">
        <div className="max-w-[1100px] mx-auto px-5 lg:px-8 py-16 lg:py-24">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-[10px] tracking-[0.3em] uppercase font-bold text-electric mb-4">
              Receipts over hype
            </p>
            <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight uppercase leading-tight">
              Choosing a supplier on price alone works fine, until it doesn't.
            </h2>
            <p className="text-base md:text-lg text-white/70 mt-6 leading-relaxed">
              The difference at Century Labs is that the documentation is there{" "}
              <span className="text-electric">before</span> you order, not missing after it arrives.
            </p>
          </div>

          {/* Live & Verifiable card */}
          <div
            className="mt-14 border border-electric/25 bg-[#0B1024] p-8 lg:p-12 sq-xl"
            data-testid="standards-live-verifiable"
          >
            <h3 className="font-display font-bold text-2xl sm:text-3xl tracking-tight text-center text-white">
              Live &amp; Verifiable
            </h3>
            <p className="text-sm text-white/50 text-center max-w-lg mx-auto mt-3 mb-10 leading-relaxed">
              Every order is backed by real, checkable data, the kind you can trace, not just trust.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
              {LIVE_VERIFIABLE.map((v, i) => {
                const Icon = v.icon;
                return (
                  <div
                    key={v.label}
                    className="flex flex-col items-center text-center fade-up"
                    style={{ animationDelay: `${0.07 * i}s` }}
                    data-testid={`verifiable-${v.label.toLowerCase().replace(/\s/g, "-")}`}
                  >
                    <div className="w-16 h-16 flex items-center justify-center rounded-full bg-electric/10 border border-electric/30 mb-4">
                      <Icon size={26} className="text-electric" />
                    </div>
                    <div className="font-display font-bold text-base uppercase tracking-tight text-white">
                      {v.label}
                    </div>
                    <p className="text-xs text-white/50 mt-2 leading-relaxed max-w-[220px]">{v.sub}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="border-t border-white/10 bg-[#070B1C]">
        <div className="max-w-[1100px] mx-auto px-5 lg:px-8 py-16 lg:py-24 text-center">
          <FileCheck size={30} className="text-electric mx-auto mb-6" />
          <h2 className="font-display font-bold text-3xl sm:text-5xl tracking-tighter uppercase leading-[0.95] max-w-3xl mx-auto">
            Buy from the supplier that shows its work.
          </h2>
          <p className="text-base text-white/60 mt-6 max-w-xl mx-auto leading-relaxed">
            Every Century Labs order ships with a lot specific Certificate of Analysis for the exact
            vial you receive, third party HPLC verified, matched to your batch. Live and verifiable,
            not a promise you can't check.
          </p>
          <div className="flex flex-wrap gap-3 justify-center mt-9">
            <Link
              to="/"
              className="btn-primary px-9 py-4 text-xs tracking-[0.2em] uppercase font-bold inline-flex items-center gap-2"
              data-testid="standards-shop-cta"
            >
              Shop Century Labs <ArrowRight size={15} />
            </Link>
            <Link
              to="/faq"
              className="btn-ghost px-9 py-4 text-xs tracking-[0.2em] uppercase font-bold inline-flex items-center"
              data-testid="standards-faq-cta"
            >
              Read the FAQ
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
